import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  of,
  shareReplay,
  Subscription,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import { IApiResponse } from '../../../shared/models/api-response';
import { IUser } from '../../../shared/models/user';
import { AuthService } from '../../../shared/services/auth.service';
import { IProjectSearchResult } from '../../projects/shared/models/project-search-result';
import { ICommittee } from '../shared/models/committee';
import { CommitteeService } from '../shared/services/committee.service';
import { UserUtility } from '../../../shared/utilities/user-utility';
import { ITab } from '../../controls/tab/tab.component';
import { ISubCommittee } from '../shared/models/sub-committee';
import { appConstants } from '../../../shared/constants/app-constants';
import { ICommitteePublishedDocumentDto } from '../shared/models/committee-published-document';
import { IInternationalMirrorCommittee } from '../shared/models/international-mirror-committee';
import { ICommitteeDeclarationOfInterest } from '../shared/models/committee-details-declaration-of-interest';
import { combineLatest } from 'rxjs';
import { ProjectService } from '../../projects/shared/services/project.service';

@Component({
    selector: 'app-committee-details',
    templateUrl: './committee-details.component.html',
    styleUrl: './committee-details.component.scss',
    standalone: false
})
export class CommitteeDetailsComponent implements OnInit, OnDestroy {
  currentUser: IUser | null = null;
  loading: boolean = true;
  loadingProjects: boolean = true;
  subscriptions: Subscription = new Subscription();

  committeeCode: string | null = null;
  committee: ICommittee | null = null;
  reloadRecentlyViewedCommittees: number = 0;
  selectedTab: string = '';
  hasAccessToDeclarationOfInterest: boolean = false;
  subCommittees: ISubCommittee[] = [];
  isShowingSubCommittees: boolean = false;
  projects: IProjectSearchResult[] = [];
  publications: ICommitteePublishedDocumentDto[] = [];
  internationalMirrorCommittee: IInternationalMirrorCommittee[] = [];
  declarationOfInterest: ICommitteeDeclarationOfInterest[] = [];

  tabIds = {
    overview: 'overview',
    internationalParticipation: 'internationalParticipation',
    committeeMembership: 'committeeMembership',
    projects: 'projects',
    publications: 'publications',
    subCommitteesAndWorkingGroups: 'subCommitteesAndWorkingGroups',
    declarationOfInterest: 'declarationOfInterest',
  };
  tabs: ITab[] = [];

  constructor(
    private _authService: AuthService,
    private _committeeService: CommitteeService,
    private _projectService: ProjectService,
    private _activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const user$ = this._authService.currentUserSubject.pipe(
      filter((u): u is IUser => u !== null)
    );

    const combinedSub = combineLatest([user$, this.committeeCode$]).subscribe(
      ([user, code]) => {
        this.currentUser = user;
        this.hasAccessToDeclarationOfInterest =
          UserUtility.isInternalUser(user) ||
          UserUtility.userIsOnCommittee(user, code);
      }
    );

    this.subscriptions.add(combinedSub);

    this.setupCommitteeProjectsSubscription();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  committeeCode$ = this._activatedRoute.paramMap.pipe(
    map((pm: ParamMap) => pm.get('code')),
    filter((code): code is string => !!code && code.trim().length > 0),
    tap((code) => {
      this.reloadRecentlyViewedCommittees++;
      this.committeeCode = code;
    }),
    shareReplay(1)
  );

  setupCommitteeProjectsSubscription() {
    const PAGE_SIZE = 10000;

    const sub = this.committeeCode$
      .pipe(
        switchMap((code) =>
          this._projectService
            .searchProjects({ committees: [code], page: 1, limit: PAGE_SIZE })
            .pipe(
              map((resp: IApiResponse<IProjectSearchResult[]>) =>
                resp?.success ? resp.content ?? [] : []
              ),
              catchError(() => of<IProjectSearchResult[]>([])),
              finalize(() => {
                this.loadingProjects = false;
              }),
              tap(() => {
                this.loadingProjects = true;
              })
            )
        )
      )
      .subscribe((projects: IProjectSearchResult[]) => {
        this.projects = projects;
      });

    this.subscriptions.add(sub);
  }

  onRecentlyViewedCommitteeFinishedLoading() {
    // getCommittee should be triggered once recent viewed committee has finish loading - so that it won't be shown as recently viewed in the page yet
    this.getCommittee();
  }

  getCommittee() {
    this.loading = true;
    if (!this.committeeCode) {
      this.loading = false;
      return;
    }

    const committeeSubscription = this._committeeService
      .getCommittee(this.committeeCode, true)
      .subscribe({
        next: (response: IApiResponse<ICommittee>) => {
          if (response.success) {
            this.committee = response.content;
            const subCommittees = this.committee?.subCommittees;
            this.declarationOfInterest = this.formattingDeclarationOfInterest(
              this.committee?.declarationOfInterest || []
            );
            this.internationalMirrorCommittee =
              this.committee?.intmirrorcommitees || [];
            if (subCommittees && subCommittees?.length > 0) {
              const activeSubCommittees = subCommittees.filter(
                (committee) =>
                  committee.committeeStatus.toLowerCase() ==
                  appConstants.committeeStatus.active.toLowerCase()
              );
              this.subCommittees = activeSubCommittees;
              this.isShowingSubCommittees =
                activeSubCommittees && activeSubCommittees.length > 0;
            } else {
              this.isShowingSubCommittees = false;
            }
          }

          this.getTabs();
          this.loading = false;
        },
        error: (_) => {
          this.loading = false;
        },
      });

    this.subscriptions.add(committeeSubscription);
  }

  formattingDeclarationOfInterest(
    declarationOfInterest: ICommitteeDeclarationOfInterest[]
  ) {
    // Format the declaration of interest data as needed
    if (!declarationOfInterest || declarationOfInterest.length === 0) {
      return [];
    }
    const updatedData = declarationOfInterest.map((item) => {
      return Object.fromEntries(
        Object.entries(item).map(([key, value]) => [
          key,
          value === '' ? '-' : value,
        ])
      ) as ICommitteeDeclarationOfInterest;
    });

    return updatedData;
  }

  onTabChange(tabId: string) {
    this.selectedTab = tabId;
  }

  getTabs() {
    this.tabs = [
      { id: this.tabIds.overview, label: 'Overview' },
      {
        id: this.tabIds.internationalParticipation,
        label: 'International Participation',
      },
      { id: this.tabIds.committeeMembership, label: 'Committee Membership' },
      { id: this.tabIds.projects, label: 'Projects' },
      { id: this.tabIds.publications, label: 'Publications' },
      {
        id: this.tabIds.subCommitteesAndWorkingGroups,
        label: 'Sub-Committees and Working Groups',
      },
      {
        id: this.tabIds.declarationOfInterest,
        label: 'Declaration of Interest',
      },
    ];

    if (!this.isShowingSubCommittees) {
      this.tabs = this.tabs.filter(
        (tab) => tab.id !== this.tabIds.subCommitteesAndWorkingGroups
      );

      if (this.selectedTab === this.tabIds.subCommitteesAndWorkingGroups) {
        this.selectedTab = this.tabIds.overview; // Reset to overview if the current tab is sub-committees
      }
    }

    if (!this.hasAccessToDeclarationOfInterest) {
      this.tabs = this.tabs.filter(
        (tab) => tab.id !== this.tabIds.declarationOfInterest
      );

      if (this.selectedTab === this.tabIds.declarationOfInterest) {
        this.selectedTab = this.tabIds.overview; // Reset to overview if the current tab is declaration of interest
      }
    }
  }
}
